import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/auth';
import MenuLateral from '../Components/MenuPrincipal/MenuLateral';
import './Grades.css'; // Shared CSS
import Swal from 'sweetalert2';

function NovaGrade() {
    const { currentUser, appUserData } = useAuth();
    const navigate = useNavigate();

    // Verifica permissão de cadastro
    React.useEffect(() => {
        if (appUserData && !appUserData['cadastrar_grades']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para cadastrar grades.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    const [descricao, setDescricao] = useState('');
    const [loading, setLoading] = useState(false);
    const [mensagem, setMensagem] = useState('');
    const [tamanhos, setTamanhos] = useState([]);
    const [currentTamanho, setCurrentTamanho] = useState('');

    const quantidadeTotal = useMemo(() =>
        tamanhos.reduce((sum, item) => sum + (Number(item.quantidade) || 0), 0),
        [tamanhos]
    );

    const handleAddTamanho = () => {
        const tamanhoFormatado = currentTamanho.trim().toUpperCase();
        if (tamanhoFormatado === '') return;

        if (tamanhos.some(t => t.tamanho === tamanhoFormatado)) {
            setMensagem(`O tamanho "${tamanhoFormatado}" já foi adicionado.`);
            return;
        }

        const novoArray = [...tamanhos, { tamanho: tamanhoFormatado, quantidade: 1 }];

        novoArray.sort((a, b) => {
            const numA = parseInt(a.tamanho, 10);
            const numB = parseInt(b.tamanho, 10);
            if (!isNaN(numA) && !isNaN(numB)) {
                return numA - numB;
            }
            return a.tamanho.localeCompare(b.tamanho);
        });

        setTamanhos(novoArray);
        setCurrentTamanho('');
        setMensagem('');
    };

    const handleUpdateQtde = (index, newQtde) => {
        const qtde = Math.max(0, Number(newQtde.replace(/\D/g, '')));
        setTamanhos(prev => prev.map((item, i) => i === index ? { ...item, quantidade: qtde } : item));
    };

    const handleRemoveTamanho = (tamanhoToRemove) => {
        setTamanhos(prev => prev.filter(item => item.tamanho !== tamanhoToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMensagem('');

        if (!descricao.trim() || tamanhos.length === 0 || quantidadeTotal <= 0) {
            setMensagem("Descrição, tamanhos e quantidade total maior que zero são obrigatórios.");
            return;
        }

        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const payload = {
                descricao: descricao.trim(),
                tamanhos: tamanhos
            };

            const API_URL = process.env.REACT_APP_API_URL;
            const response = await fetch(`${API_URL}/api/grades`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Falha ao salvar a grade.");
            }

            navigate('/app/grades');
        } catch (error) {
            console.error("Erro ao adicionar grade: ", error);
            setMensagem(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grades-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />
            <div className="content-with-sidebar">
                <div className="grades-header">
                    <div className="d-flex align-items-center gap-3">
                        <button className="btn btn-link text-secondary p-0" onClick={() => navigate('/app/grades')} title="Voltar">
                            <i className="fas fa-arrow-left fa-lg"></i>
                        </button>
                        <h1 className="grades-title m-0">
                            Nova <span className="highlight">Grade</span>
                        </h1>
                    </div>
                </div>

                <div className="grades-content">
                    {mensagem && <div className="alert alert-danger mb-4 rounded-3 shadow-sm">{mensagem}</div>}

                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-5">
                                <div className="form-card">
                                    <h5 className="form-section-header">Dados da Grade</h5>
                                    <div className="mb-3">
                                        <label htmlFor="descricao" className="form-label-custom">Descrição (Ex: 34 ao 39 / P, M, G)</label>
                                        <input
                                            type="text"
                                            className="form-control-custom"
                                            id="descricao"
                                            value={descricao}
                                            onChange={(e) => setDescricao(e.target.value)}
                                            required
                                            placeholder="Digite a descrição da grade"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="inputTamanho" className="form-label-custom">Adicionar Tamanho</label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control-custom rounded-end-0"
                                                id="inputTamanho"
                                                value={currentTamanho}
                                                onChange={(e) => setCurrentTamanho(e.target.value)}
                                                onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTamanho(); } }}
                                                placeholder="Ex: P, M, G ou 34"
                                            />
                                            <button className="btn btn-primary-custom rounded-start-0 m-0" type="button" onClick={handleAddTamanho}>Adicionar</button>
                                        </div>
                                    </div>
                                    <hr className="text-muted opacity-25 my-4" />
                                    <div>
                                        <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                                            <h6 className="mb-0 text-secondary">Quantidade total da grade:</h6>
                                            <span className="badge bg-success fs-6">{quantidadeTotal}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-7">
                                <div className="form-card">
                                    <h5 className="form-section-header">Configuração de Quantidades</h5>
                                    {tamanhos.length === 0 ? (
                                        <div className="text-center p-5 text-muted bg-light rounded">
                                            <i className="fas fa-ruler-combined fa-2x mb-2 opacity-50"></i>
                                            <p>Adicione tamanhos para configurar as quantidades.</p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-custom table-sm">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '60%' }}>Tamanho</th>
                                                        <th className="text-center">Qtde. Padrão</th>
                                                        <th className="text-center">Ação</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tamanhos.map((item, index) => (
                                                        <tr key={item.tamanho}>
                                                            <td className="align-middle fw-bold">{item.tamanho}</td>
                                                            <td className="text-center">
                                                                <input
                                                                    type="number"
                                                                    className="form-control-custom form-control-sm text-center d-inline-block"
                                                                    value={item.quantidade}
                                                                    onChange={(e) => handleUpdateQtde(index, e.target.value)}
                                                                    min="0"
                                                                    style={{ width: '80px' }}
                                                                />
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-delete"
                                                                    onClick={() => handleRemoveTamanho(item.tamanho)}
                                                                    title="Remover Tamanho"
                                                                >
                                                                    <i className="fas fa-trash-alt"></i>
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form-actions-bar">
                            <Link to="/app/grades" className="btn-secondary-custom">Cancelar</Link>
                            <button type="submit" className="btn-primary-custom" disabled={loading}>
                                {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Salvando...</> : 'Salvar Grade'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default NovaGrade;